import Input from "components/atoms/input/Input";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { AutoInputStyled } from "./UtilityStyled";
import { toTrim } from "utils/functions";

type Props = {
  onChange: (value: string) => void;
};

const SEG_MAX_LEN = [2, 2, 2, 3] as const;

const buildTimeString = (parts: string[]) => {
  const hh = parts[0] ?? "";
  const mm = parts[1] ?? "";
  const ss = parts[2] ?? "";
  const zzz = parts[3] ?? "";

  let result = "";

  if (hh) {
    result += hh;
  }
  if (mm) {
    result += `:${mm}`;
  }
  if (ss) {
    result += `:${ss}`;
  }
  if (zzz) {
    result += `.${zzz}`;
  }

  return result;
};

const splitPastedTime = (pasted: string) => {
  const digits = pasted.replace(/\D/g, "").slice(0, 9);

  const hh = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const ss = digits.slice(4, 6);
  const zzz = digits.slice(6, 9);

  return [hh, mm, ss, zzz];
};

const PLACEHOLDERS = ["hh", "mm", "ss", "zzz"] as const;

const clampNumber = (num: number, min: number, max: number) => {
  if (num < min) return min;
  if (num > max) return max;
  return num;
};

const clamp2DigitsIfComplete = (val: string, max: number) => {
  if (val.length !== 2) return val;

  const n = Number(val);
  if (Number.isNaN(n)) return "";

  const clamped = clampNumber(n, 0, max);
  return String(clamped).padStart(2, "0");
};

export default function TimeInput(props: Props) {
  const [values, setValues] = useState<string[]>(["", "", "", ""]); // hh, mm, ss, zzz
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const maxLen = SEG_MAX_LEN[index] ?? 2;

    let val = e.target.value;
    val = val.replace(/\D/g, ""); // 숫자만
    if (val.length > maxLen) {
      val = val.slice(0, maxLen);
    }

    // ✅ 2자리 완성되면 범위 자동 보정
    if (index === 0) {
      // hh 00~23
      val = clamp2DigitsIfComplete(val, 23);
    }
    if (index === 1 || index === 2) {
      // mm/ss 00~59
      val = clamp2DigitsIfComplete(val, 59);
    }

    const newValues = [...values];
    newValues[index] = val;
    setValues(newValues);

    // 칸이 꽉 차면 다음 칸으로 이동
    if (val.length === maxLen) {
      const next = inputsRef.current[index + 1];
      if (next) {
        next.focus();
      }
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key !== "Backspace") return;

    if (!values[index]) {
      const prevIndex = index - 1;
      if (prevIndex < 0) return;

      e.preventDefault();
      const prev = inputsRef.current[prevIndex];
      if (prev) {
        prev.focus();
        prev.select();
      }
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = toTrim(e.clipboardData.getData("text"));
    if (!pasted) return;

    const parts = splitPastedTime(pasted);

    const hasAny = parts.some((p) => p.length > 0);
    if (!hasAny) return;

    e.preventDefault();

    const nextValues = [...parts];

    nextValues[0] = clamp2DigitsIfComplete(nextValues[0] ?? "", 23);
    nextValues[1] = clamp2DigitsIfComplete(nextValues[1] ?? "", 59);
    nextValues[2] = clamp2DigitsIfComplete(nextValues[2] ?? "", 59);

    // zzz는 숫자만/길이만 맞추면 됨
    nextValues[3] = (nextValues[3] ?? "").replace(/\D/g, "").slice(0, 3);

    setValues(nextValues);

    // 마지막으로 채워진 칸으로 포커스
    let focusIndex = 0;
    for (let i = 0; i < nextValues.length; i += 1) {
      if (nextValues[i]) {
        focusIndex = i;
      }
    }

    const el = inputsRef.current[focusIndex];
    if (el) {
      el.focus();
    }
  };

  useEffect(() => {
    props.onChange(buildTimeString(values));
  }, [values]);

  return (
    <AutoInputStyled.Container>
      {values.map((val, i) => {
        let sep: React.ReactNode = null;

        if (i === 0 || i === 1) {
          sep = <AutoInputStyled.Separator>:</AutoInputStyled.Separator>;
        }
        if (i === 2) {
          sep = <AutoInputStyled.Separator>.</AutoInputStyled.Separator>;
        }

        return (
          <Fragment key={i}>
            <Input
              autoComplete="off"
              name="time"
              inputMode="numeric"
              placeholder={PLACEHOLDERS[i]}
              maxLength={SEG_MAX_LEN[i]}
              value={val}
              ref={(el) => (inputsRef.current[i] = el)}
              onChange={(e) => handleChange(i, e)}
              onKeyDown={(e) => handleKeyDown(i, e)} // ✅ 추가
              onFocus={handleFocus}
              onPaste={handlePaste}
              data-testid="maintenance-admin-unlock-admin-unlock-time-change"
            />

            {i < values.length - 1 && sep}
          </Fragment>
        );
      })}
    </AutoInputStyled.Container>
  );
}
